import React, { useState } from 'react';

import AutoCompleteAddressFormGroup from 'components/form/autocompleted_address_form_group';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import Modal from 'components/modal';
import Options from 'components/form/managed_input/options';
import { Button } from 'components/ui/button';
import { ContactRoleInput } from 'bundles/backoffice/contacts/add';

import type { Country } from 'bundles/research/index/types';
import type { NewProperty } from './types';
import type { Role } from 'bundles/backoffice/contacts/types';
import { Alert, Row } from 'react-bootstrap-5';

interface InsightsSearchModalProps {
  contactRole?: Role | null;
  contactRoleOptions?: Role[];
  countryList: Country[];
  error?: string;
  info?: string;
  property: NewProperty;
  onChange: (value: NewProperty) => void;
  onContactRoleChange?: (role: Role) => void;
  onHide: () => void;
  onSearch: () => Promise<void>;
}

export default function InsightsSearchModal({
  contactRole,
  contactRoleOptions,
  countryList,
  error,
  info,
  property,
  onChange,
  onContactRoleChange,
  onHide,
  onSearch,
}: InsightsSearchModalProps) {
  const [searching, setSearching] = useState(false);
  const handleSearch = async () => {
    setSearching(true);
    await onSearch();
    setSearching(false);
  };
  const changeCountry = (value: string) => {
    const country = countryList.find(country => country.id === parseInt(value));
    if (country) {
      const state = country.countryStates.length > 0 ? country.countryStates[0].code : '';
      onChange({ country, state });
    }
  };
  const validState = property.country.countryStates.length > 0 ? !!property.state : true;
  const validZip = property.country.zipLabel ? !!property.zip : true;
  const blankField = !property.address || !property.city  || !property.country.id || !validState || !validZip;

  return (
    <React.Fragment>
      <Modal.Header closeButton>
        Create a New Property
      </Modal.Header>
      <Modal.Body>
        {info && (
          <div className="mb-3">
            <Alert>{info}</Alert>
          </div>
        )}
        {error && (
          <div className="mb-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}
        <p>
          Search for a property and Buildout Insights will auto-populate the property information we have,
          or you can create a brand new property.
        </p>
        <div className="form-vertical-field-container">
          {onContactRoleChange && (
            <ContactRoleInput
              role={contactRole ?? null}
              roleOptions={contactRoleOptions}
              onChange={onContactRoleChange}
            />
          )}
          {countryList.length > 1 && (
            <ManagedFormGroup
              label="Country"
              name="country"
              required
              type="select"
              value={property.country.id}
              onValueChange={changeCountry}
            >
              <Options labelName="name" options={countryList} valueName="id"/>
            </ManagedFormGroup>
          )}
          <AutoCompleteAddressFormGroup
            address={property.address}
            autocomplete="off"
            city={property.city}
            country={property.country}
            required
            state={property.state}
            zip={property.zip}
            onChange={onChange}
          />
          <ManagedFormGroup
            label="City"
            name="city"
            required
            value={property.city}
            onChange={onChange}
          />
          <Row className="align-items-end">
            {!!property.country.countryStates?.length && (
              <ManagedFormGroup
                className="col-md-8"
                inputClassName="form-select"
                label={property.country.stateLabel}
                name="state"
                required
                type="select"
                value={property.state}
                onChange={onChange}
              >
                <Options labelName="displayName" options={property.country.countryStates} valueName="code"/>
              </ManagedFormGroup>
            )}
            {property.country.zipLabel && (
              <ManagedFormGroup
                className="col-md-4"
                label={property.country.zipLabel}
                name="zip"
                required
                value={property.zip}
                onChange={onChange}
              />
            )}
          </Row>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button label="Cancel" variant="ghost" onClick={onHide}/>
        <Button disabled={Boolean(searching || info || blankField)} onClick={handleSearch}>
          {info ? 'Creating Property...' : 'Search'}
        </Button>
      </Modal.Footer>
    </React.Fragment>
  );
}
